(() => {
  const E = window.Ether || {};
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Money ---------- */

  function formatMoney(cents) {
    const fmt = E.moneyFormat || '${{amount}}';
    const n = Number(cents) / 100;
    const withDelims = (num, dec, thou, sep) =>
      num.toFixed(dec).replace(/\B(?=(\d{3})+(?!\d))/g, thou).replace(/\.(\d+)$/, sep + '$1');
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return withDelims(n, 0, ',', '.');
      if (key === 'amount_with_comma_separator') return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, '.').replace(/\.(\d{2})$/, ',$1');
      if (key === 'amount_no_decimals_with_comma_separator') return withDelims(n, 0, '.', ',');
      return withDelims(n, 2, ',', '.');
    });
  }

  /* ---------- Header ---------- */

  const header = $('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', scrollY > 8);
    addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  const menuBtn = $('[data-menu-toggle]');
  const mobileNav = $('#mobile-nav');
  menuBtn?.addEventListener('click', () => {
    const open = menuBtn.getAttribute('aria-expanded') !== 'true';
    menuBtn.setAttribute('aria-expanded', open);
    mobileNav.classList.toggle('is-open', open);
    document.body.style.overflow = open ? 'hidden' : '';
  });

  /* ---------- Reveal on scroll ---------- */

  const revealIO = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        e.target.classList.add('is-in');
        revealIO.unobserve(e.target);
      }
    });
  }, { rootMargin: '0px 0px -8% 0px' });
  const observeReveals = (root = document) => $$('.reveal:not(.is-in)', root).forEach((el) => revealIO.observe(el));
  observeReveals();

  /* ---------- Tilt, glare + foil ----------
     One function poses a card from a normalized point (0–1, 0–1). Three inputs feed it:
     - Mouse/pen: the card leans toward the cursor.
     - Touch drag: product stage only, horizontal, so vertical scroll is never hijacked.
     - Phone motion: every card on screen catches light as you tilt the phone,
       which is how most people arrive (from TikTok, on a phone). */

  const clamp01 = (n) => Math.min(Math.max(n, 0), 1);

  function pose(stage, px, py, active = true) {
    const strength = Number(stage.dataset.tiltStrength || 10);
    stage.classList.toggle('is-active', active);
    stage.style.setProperty('--ry', `${active ? (px - 0.5) * strength * 2 : 0}deg`);
    stage.style.setProperty('--rx', `${active ? (0.5 - py) * strength * 2 : 0}deg`);
    stage.style.setProperty('--px', px);
    stage.style.setProperty('--py', py);
    stage.style.setProperty('--gx', `${px * 100}%`);
    stage.style.setProperty('--gy', `${py * 100}%`);
    stage.style.setProperty('--glare', active ? 1 : 0);
  }

  const onScreen = new Set();
  const screenIO = new IntersectionObserver((entries) =>
    entries.forEach((e) => (e.isIntersecting ? onScreen.add(e.target) : onScreen.delete(e.target)))
  );

  function bindTilt(stage) {
    if (reduceMotion || stage.dataset.tiltBound) return;
    stage.dataset.tiltBound = '1';
    screenIO.observe(stage);
    const area = stage.closest('.p-card__media, .product__stage, .hero') || stage;
    const allowTouch = area.classList.contains('product__stage');
    let raf = 0;

    area.addEventListener('pointermove', (e) => {
      if (e.pointerType === 'touch' && !allowTouch) return;
      const r = area.getBoundingClientRect();
      const px = clamp01((e.clientX - r.left) / r.width);
      const py = clamp01((e.clientY - r.top) / r.height);
      stage.dataset.pointer = '1';
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => pose(stage, px, py));
      if (allowTouch) area.classList.add('has-moved');
    });
    const leave = () => {
      cancelAnimationFrame(raf);
      delete stage.dataset.pointer;
      if (!gyro.on) pose(stage, 0.5, 0.5, false);
    };
    area.addEventListener('pointerleave', leave);
    area.addEventListener('pointercancel', leave);
    area.addEventListener('pointerup', (e) => e.pointerType === 'touch' && leave());
  }
  const bindTilts = (root = document) => $$('[data-tilt]', root).forEach(bindTilt);
  bindTilts();

  // Phone motion. Android grants it outright; iOS needs a tap first, so the
  // product stage hint invites one and that tap asks for permission.
  const gyro = { on: false, base: null, x: 0.5, y: 0.5, raf: 0 };
  const isTouch = matchMedia('(hover: none) and (pointer: coarse)').matches;
  const needsPermission = typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function';

  function onOrient(e) {
    if (e.beta == null || e.gamma == null) return;
    if (!gyro.on) {
      gyro.on = true;
      document.documentElement.classList.add('has-gyro');
    }
    // Calibrate to however the phone is being held, and drift toward it slowly.
    gyro.base = gyro.base == null ? e.beta : gyro.base + (e.beta - gyro.base) * 0.01;
    const tx = clamp01(0.5 + e.gamma / 50);
    const ty = clamp01(0.5 + (e.beta - gyro.base) / 40);
    cancelAnimationFrame(gyro.raf);
    gyro.raf = requestAnimationFrame(() => {
      gyro.x += (tx - gyro.x) * 0.25;
      gyro.y += (ty - gyro.y) * 0.25;
      onScreen.forEach((s) => s.dataset.pointer || pose(s, gyro.x, gyro.y));
    });
  }

  function startGyro() {
    if (gyro.listening || reduceMotion) return;
    gyro.listening = true;
    addEventListener('deviceorientation', onOrient);
  }

  if (isTouch && typeof DeviceOrientationEvent !== 'undefined') {
    if (needsPermission) {
      const hint = $('.product__hint');
      if (hint) hint.textContent = 'Tap, then tilt your phone';
      $('[data-stage]')?.addEventListener('click', () => {
        DeviceOrientationEvent.requestPermission().then((s) => s === 'granted' && startGyro()).catch(() => {});
      }, { once: true });
    } else {
      startGyro();
      const hint = $('.product__hint');
      if (hint) hint.textContent = 'Tilt your phone';
    }
  }

  /* ---------- Quantity steppers ---------- */

  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-step]');
    if (!step) return;
    const input = step.parentElement.querySelector('[data-qty-input]');
    const min = Number(input.min || 0);
    input.value = Math.max(min, Number(input.value || 0) + Number(step.dataset.qtyStep));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Cart drawer ---------- */

  let lastFocus = null;

  const drawer = () => $('[data-cart-drawer]');

  function openDrawer() {
    const d = drawer();
    if (!d) return (location.href = E.routes.cart);
    if (d.classList.contains('is-open')) return;
    lastFocus = document.activeElement;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => $('.drawer__panel', d).focus());
  }

  function closeDrawer() {
    const d = drawer();
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    lastFocus?.focus?.();
  }

  function renderCart(sections) {
    const html = sections?.['cart-drawer'];
    if (!html) return;
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer]');
    const d = drawer();
    if (!next || !d) return;
    $('.drawer__panel', d).innerHTML = $('.drawer__panel', next).innerHTML;
    d.dataset.count = next.dataset.count;
    const count = Number(next.dataset.count);
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count > 0 ? count : '';
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      el.classList.add('is-bumped');
    });
  }

  async function cartRequest(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: 'cart-drawer', sections_url: location.pathname }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong');
    return data;
  }

  async function addToCart(id, quantity = 1) {
    const data = await cartRequest(`${E.routes.cartAdd}.js`, { items: [{ id: Number(id), quantity: Number(quantity) }] });
    renderCart(data.sections);
    openDrawer();
  }

  async function changeLine(line, quantity) {
    const data = await cartRequest(`${E.routes.cartChange}.js`, { line: Number(line), quantity: Number(quantity) });
    if ($('[data-cart-lines]')) return location.reload();
    renderCart(data.sections);
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-open]')) {
      if (!drawer() || document.body.classList.contains('template-cart')) return;
      e.preventDefault();
      openDrawer();
    }
    if (e.target.closest('[data-drawer-close]')) closeDrawer();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer()?.classList.contains('is-open')) closeDrawer();
  });

  // Line item edits — work in both the drawer and the cart page.
  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-remove]');
    if (!remove) return;
    const line = remove.closest('[data-line]');
    line.classList.add('is-updating');
    changeLine(line.dataset.line, 0).catch(() => line.classList.remove('is-updating'));
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest('.line [data-qty-input]');
    if (!input) return;
    const line = input.closest('[data-line]');
    line.classList.add('is-updating');
    changeLine(line.dataset.line, input.value).catch((err) => {
      line.classList.remove('is-updating');
      alert(err.message);
    });
  });

  // Quick add from product cards.
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    btn.classList.add('is-loading');
    try {
      await addToCart(btn.dataset.quickAdd, 1);
      btn.classList.add('is-added');
      setTimeout(() => btn.classList.remove('is-added'), 1600);
    } catch (err) {
      alert(err.message);
    } finally {
      btn.classList.remove('is-loading');
    }
  });

  /* ---------- Product page ---------- */

  const productEl = $('[data-product]');
  if (productEl) {
    const form = $('[data-product-form]', productEl);
    const idInput = $('[data-variant-id]', productEl);
    const variants = JSON.parse($('[data-product-json]')?.textContent || '[]');
    const addButtons = $$('[data-add-button]');
    const stageEl = $('[data-stage]', productEl);

    // Gallery
    const slides = $$('[data-slide]', productEl);
    const thumbs = $$('[data-thumb]', productEl);
    const show = (i) => {
      slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
      thumbs.forEach((t, n) => t.setAttribute('aria-current', n === i));
    };
    thumbs.forEach((t) => t.addEventListener('click', () => show(Number(t.dataset.thumb))));

    // Variant picker
    const setAvailability = (variant) => {
      addButtons.forEach((b) => {
        b.disabled = !variant || !variant.available;
        const label = $('[data-add-label]', b);
        if (label) label.textContent = !variant ? 'Unavailable' : variant.available ? 'Add to bag' : 'Sold out';
      });
    };
    $$('[data-option]', productEl).forEach((input) =>
      input.addEventListener('change', () => {
        const chosen = $$('[data-option]:checked', productEl).map((i) => i.value);
        const variant = variants.find((v) => v.options.every((o, i) => o === chosen[i]));
        $$('[data-option-label]', productEl).forEach((el, i) => (el.textContent = chosen[i]));
        setAvailability(variant);
        if (!variant) return;
        idInput.value = variant.id;
        $$('[data-price-current]').forEach((el) => (el.textContent = formatMoney(variant.price)));
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState(null, '', url);
        if (variant.featured_media) {
          const idx = slides.findIndex((s) => s.dataset.mediaId === String(variant.featured_media.id));
          if (idx > -1) show(idx);
        }
      })
    );

    // Add to cart
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const qty = $('[data-qty-input]', form)?.value || 1;
      addButtons.forEach((b) => b.setAttribute('aria-busy', 'true'));
      try {
        await addToCart(idInput.value, qty);
      } catch (err) {
        $$('[data-add-label]').forEach((l) => {
          const prev = l.textContent;
          l.textContent = err.message;
          setTimeout(() => (l.textContent = prev), 2400);
        });
      } finally {
        addButtons.forEach((b) => b.removeAttribute('aria-busy'));
      }
    });

    // Sticky add-to-bag appears once the main button scrolls away.
    const sticky = $('[data-sticky-buy]');
    const mainBtn = $('[data-add-button]', form);
    if (sticky && mainBtn) {
      // Scroll position, not IntersectionObserver: a jump straight to the top
      // (iOS status-bar tap) skips the intersecting state and would strand the bar.
      let shown = false;
      const update = () => {
        const visible = mainBtn.getBoundingClientRect().bottom < 0;
        if (visible === shown) return;
        shown = visible;
        sticky.classList.toggle('is-visible', visible);
        sticky.setAttribute('aria-hidden', !visible);
        $('button', sticky).tabIndex = visible ? 0 : -1;
      };
      addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      update();
    }

    stageEl?.addEventListener('pointerdown', () => stageEl.classList.add('has-moved'), { once: true });

    // Finish preview: switching to holo sweeps the light across the card once,
    // so the change reads even when nobody is moving the mouse or phone.
    $$('[data-finish]', productEl).forEach((btn) =>
      btn.addEventListener('click', () => {
        const finish = btn.dataset.finish;
        $$('[data-finish]', productEl).forEach((b) => b.setAttribute('aria-pressed', b === btn));
        stageEl.dataset.preview = finish;
        if (finish !== 'holo' || reduceMotion || gyro.on) return;
        const stage = $('.product__slide.is-active [data-tilt]', stageEl);
        if (!stage || stage.dataset.pointer) return;
        const t0 = performance.now();
        const sweep = (t) => {
          const k = Math.min((t - t0) / 900, 1);
          const e = 1 - Math.pow(1 - k, 3);
          pose(stage, 0.1 + e * 0.8, 0.35 + Math.sin(e * Math.PI) * 0.2, k < 1);
          if (k < 1) requestAnimationFrame(sweep);
        };
        requestAnimationFrame(sweep);
      })
    );
  }

  /* ---------- Recommendations ---------- */

  const recs = $('[data-recommendations]');
  if (recs && recs.dataset.url) {
    fetch(recs.dataset.url)
      .then((r) => r.text())
      .then((html) => {
        const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
        if (!next || !next.innerHTML.trim()) return;
        recs.innerHTML = next.innerHTML;
        bindTilts(recs);
      })
      .catch(() => {});
  }

  /* ---------- Video wall: play only what's on screen ---------- */

  const reelIO = new IntersectionObserver((entries) =>
    entries.forEach((e) => {
      const v = e.target;
      if (e.isIntersecting && !reduceMotion) v.play().catch(() => {});
      else v.pause();
    }), { threshold: 0.5 }
  );
  $$('[data-reels] video').forEach((v) => reelIO.observe(v));

  /* ---------- Collection sort ---------- */

  $('[data-sort]')?.addEventListener('change', (e) => {
    const url = new URL(location.href);
    url.searchParams.set('sort_by', e.target.value);
    url.searchParams.delete('page');
    location.href = url;
  });
})();
